<template>
	<v-dialog
		:value="value"
		fullscreen
		hide-overlay
		persistent
		transition="dialog-bottom-transition"
		content-class="haq-dialog halo-console-dialog"
		@input="$emit('input', $event)"
	>
		<div v-if="state" class="halo-console">
			<header class="hc-top">
				<h2 class="hc-modal-title">Configure Virtual Edge Device</h2>
				<div class="hc-device-select">
					<HaqAutocomplete
						v-model="selectedFleetId"
						:items="fleetItems"
						item-text="name"
						item-value="id"
						hide-details
						dense
						clearable
						placeholder="Search HTTP devices"
						:menu-props="{ bottom: true, offsetY: true, closeOnClick: true, closeOnContentClick: true, auto: true }"
					/>
				</div>
				<div class="hc-top-status">
					<span class="hc-online">
						<span class="hc-dot ok" />
						Online
					</span>
					<v-btn icon class="hc-close" @click="close">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</div>
			</header>

			<div class="hc-body">
				<aside class="hc-rail">
					<button
						v-for="item in primaryNav"
						:key="item.id"
						type="button"
						class="hc-rail-item"
						:class="{ 'is-active': section === item.id }"
						@click="goSection(item.id)"
					>
						<v-icon small>{{ item.icon }}</v-icon>
						<span>{{ item.label }}</span>
					</button>
					<div class="hc-rail-divider" />
					<button
						type="button"
						class="hc-rail-item is-muted"
						:class="{ 'is-active': section === 'advanced' }"
						@click="goSection('advanced')"
					>
						<v-icon small>mdi-cog-outline</v-icon>
						<span>Advanced</span>
					</button>
				</aside>

				<main class="hc-main">
					<p v-if="readOnlyMode" class="hc-banner-warn mb-4">
						Read only mode is on. Edits stay in the console and are not sent to the device.
					</p>
					<!-- Overview -->
					<section v-show="section === 'overview'">
						<div class="hc-section-head">
							<div>
								<p class="hc-eyebrow">{{ state.tenant }}</p>
								<h1 class="hc-title hc-mono">{{ state.device.name }}</h1>
								<p class="hc-subtitle">
									<v-icon small class="mr-1">mdi-map-marker-outline</v-icon>
									{{ state.device.location }} · {{ state.device.model }}
								</p>
							</div>
							<div class="hc-actions">
								<div class="hc-readonly">
									<v-switch
										v-model="readOnlyMode"
										label="Read Only Mode"
										color="primary"
										hide-details
										dense
										class="mt-0 pt-0"
										:disabled="readOnlySaving"
										@change="onReadOnlyModeChange"
									/>
									<v-tooltip bottom max-width="280">
										<template v-slot:activator="{ on, attrs }">
											<v-icon
												small
												class="hc-readonly-info"
												v-bind="attrs"
												v-on="on"
											>mdi-information-outline</v-icon>
										</template>
										<span>
											When on, this console will not send configuration changes, locate, presets, reboot, or reset to the device. Live reads still load.
										</span>
									</v-tooltip>
								</div>
								<v-btn
									class="haq-btn haq-btn-secondary"
									:loading="refreshing"
									:disabled="refreshing"
									@click="refreshOverview"
								>
									<v-icon left small>mdi-refresh</v-icon>
									Refresh
								</v-btn>
								<v-btn
									class="haq-btn haq-btn-secondary"
									:disabled="configLoading"
									@click="openIdentityModal"
								>
									Edit info
								</v-btn>
								<v-menu offset-y left content-class="hc-options-menu">
									<template v-slot:activator="{ on, attrs }">
										<v-btn
											class="haq-btn haq-btn-secondary"
											v-bind="attrs"
											v-on="on"
										>
											Options
											<v-icon right small>mdi-chevron-down</v-icon>
										</v-btn>
									</template>
									<v-list dense>
										<v-list-item
											:disabled="finding || (!!sliceLoading.versions && liveMode)"
											@click="findDevice"
										>
											<v-list-item-icon class="mr-2">
												<v-icon small>mdi-map-marker-radius</v-icon>
											</v-list-item-icon>
											<v-list-item-title>
												{{ finding ? 'Finding…' : 'Find this device' }}
											</v-list-item-title>
										</v-list-item>
										<v-list-item @click="openPresetsModal">
											<v-list-item-icon class="mr-2">
												<v-icon small>mdi-playlist-check</v-icon>
											</v-list-item-icon>
											<v-list-item-title>Presets</v-list-item-title>
										</v-list-item>
										<v-list-item @click="openConfirm('restart')">
											<v-list-item-icon class="mr-2">
												<v-icon small>mdi-restart</v-icon>
											</v-list-item-icon>
											<v-list-item-title>Restart</v-list-item-title>
										</v-list-item>
									</v-list>
								</v-menu>
							</div>
						</div>
						<p v-if="liveError" class="hc-banner is-error mb-4">{{ liveError }}</p>

						<div class="hc-grid-4">
							<template v-if="healthLoading">
								<div v-for="n in 4" :key="'hskel-' + n" class="hc-card hc-skel">
									<div class="hc-skel-line short" />
									<div class="hc-skel-line" />
									<div class="hc-skel-line mid" />
								</div>
							</template>
							<template v-else>
								<div
									v-for="card in healthCards"
									:key="card.key"
									class="hc-card hc-status-card"
									:class="statusClass(card.status)"
								>
									<div class="hc-status-label">
										<span class="hc-dot" :class="card.status" />
										{{ card.label }}
									</div>
									<div class="hc-status-title">{{ card.title }}</div>
									<div class="hc-status-detail">{{ card.detail }}</div>
								</div>
							</template>
						</div>

						<div class="hc-panel-title">
							<h3>Live readings</h3>
							<span v-if="readingsLoading" class="hc-inline-load">
								<v-progress-circular indeterminate size="14" width="2" color="primary" />
								Loading
							</span>
						</div>
						<div class="hc-grid-readings">
							<template v-if="readingsLoading">
								<div v-for="n in 12" :key="'rskel-' + n" class="hc-card hc-reading hc-skel">
									<div class="hc-skel-circle" />
									<div class="hc-skel-line short center" />
									<div class="hc-skel-line mid center" />
								</div>
							</template>
							<template v-else-if="!state.readings.length">
								<p class="hc-hint mb-0">No live readings on this device.</p>
							</template>
							<template v-else>
								<div v-for="r in state.readings" :key="r.key" class="hc-card hc-reading">
									<v-icon>{{ r.icon }}</v-icon>
									<div class="hc-reading-label">{{ r.label }}</div>
									<div class="hc-reading-value hc-mono" :class="{ 'is-good': r.good }">
										{{ r.value }}<span v-if="r.unit" class="hc-reading-unit">{{ r.unit }}</span>
									</div>
								</div>
							</template>
						</div>

						<div class="hc-card">
							<div class="hc-panel-title">
								<h3>Device facts</h3>
								<span v-if="factsLoading" class="hc-inline-load">
									<v-progress-circular indeterminate size="14" width="2" color="primary" />
									Loading
								</span>
							</div>
							<div v-if="factsLoading" class="hc-facts">
								<div v-for="n in 5" :key="'fskel-' + n">
									<div class="hc-skel-line short" />
									<div class="hc-skel-line mid mt-2" />
								</div>
							</div>
							<div v-else class="hc-facts">
								<div v-for="fact in deviceFacts" :key="fact.label">
									<div class="hc-fact-label">{{ fact.label }}</div>
									<div class="hc-fact-value hc-mono">{{ fact.value }}</div>
								</div>
							</div>
						</div>
					</section>

					<!-- Integrations -->
					<section v-show="section === 'integrations'">
						<div class="hc-section-head">
							<h1 class="hc-title mb-0">Integrations</h1>
							<span v-if="configLoading" class="hc-inline-load">
								<v-progress-circular indeterminate size="16" width="2" color="primary" />
								Loading
							</span>
						</div>
						<div v-if="configLoading" class="hc-card hc-skel hc-skel-block">
							<div class="hc-skel-line" />
							<div class="hc-skel-line mid" />
							<div class="hc-skel-line" />
						</div>
						<template v-else>

						<div class="hc-tabs">
							<button
								v-for="tab in integrationTabs"
								:key="tab.id"
								type="button"
								class="hc-tab"
								:class="{ 'is-active': integrations.activeTab === tab.id }"
								@click="onIntegrationTab(tab.id)"
							>
								<div class="hc-tab-name">
									<span class="hc-dot" :class="tab.status" />
									{{ tab.label }}
								</div>
								<div class="hc-tab-meta">{{ tab.statusLabel }}</div>
							</button>
						</div>

						<div class="hc-card">
							<div
								v-if="activeIntegration.error"
								class="hc-banner is-error"
							>
								{{ activeIntegration.error }}
							</div>

							<div class="d-flex align-center mb-4">
								<v-switch
									v-model="activeIntegration.enabled"
									label="Enabled"
									color="primary"
									hide-details
									dense
									class="mt-0"
								/>
							</div>

							<div class="hc-grid-2">
								<HaqTextField v-model="activeIntegration.host" label="Host" />
								<HaqTextField v-model="activeIntegration.port" label="Port" />
								<HaqTextField
									v-model="activeIntegration.endpoint"
									:label="integrations.activeTab === 'heartbeat' ? 'Message endpoint' : 'Event endpoint'"
								/>
								<HaqSelect
									v-model="activeIntegration.protocol"
									:items="state.protocolOptions"
									label="Protocol"
									hide-details
								/>
								<HaqTextField
									v-if="integrations.activeTab === 'heartbeat'"
									v-model="activeIntegration.interval"
									label="Interval seconds"
								/>
								<HaqTextField
									v-model="activeIntegration.retryHoldoff"
									label="Retry holdoff seconds"
								/>
							</div>

							<button
								type="button"
								class="hc-expand-toggle mt-4"
								@click="authExpanded = !authExpanded"
							>
								<span>
									<v-icon small class="mr-1">mdi-lock-outline</v-icon>
									Authentication
								</span>
								<v-icon small>{{ authExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
							</button>
							<div v-show="authExpanded" class="hc-card hc-auth-panel mt-2">
								<HaqSelect
									v-model="activeIntegration.authMode"
									:items="state.authModeOptions || ['Basic', 'Bearer', 'OAuth2']"
									label="Auth mode"
									hide-details
									class="mb-4"
								/>
								<div v-if="activeIntegration.authMode === 'Bearer'" class="hc-grid-2">
									<HaqTextField
										v-model="activeIntegration.bearerToken"
										label="Bearer token"
										type="password"
									/>
									<HaqTextField
										v-model="activeIntegration.bearerAuthUrl"
										label="Bearer auth URL"
									/>
								</div>
								<div v-else-if="activeIntegration.authMode === 'OAuth2'" class="hc-grid-2">
									<HaqTextField
										v-model="activeIntegration.oauthAuthUrl"
										label="OAuth auth URL"
									/>
									<HaqTextField
										v-model="activeIntegration.refreshTokenKey"
										label="Refresh token key"
									/>
									<HaqTextField
										v-model="activeIntegration.refreshToken"
										label="Refresh token"
										type="password"
									/>
								</div>
								<div v-else class="hc-grid-2">
									<HaqTextField v-model="activeIntegration.username" label="Username" />
									<HaqTextField
										v-model="activeIntegration.password"
										label="Password"
										type="password"
									/>
								</div>
							</div>

							<div class="hc-footer-row">
								<span />
								<v-btn
									class="haq-btn haq-btn-primary"
									:loading="integrationSaving"
									@click="saveIntegration"
								>
									<v-icon left small>mdi-content-save</v-icon>
									Save changes
								</v-btn>
							</div>
						</div>
						</template>
					</section>

					<!-- Events -->
					<section v-show="section === 'events'">
						<div class="hc-section-head">
							<div>
								<h1 class="hc-title">Events</h1>
								<p class="hc-subtitle">
									{{ state.events.configured }} configured, {{ enabledEventCount }} enabled.
								</p>
							</div>
							<span v-if="configLoading" class="hc-inline-load">
								<v-progress-circular indeterminate size="16" width="2" color="primary" />
								Loading
							</span>
						</div>
						<div v-if="configLoading" class="hc-card hc-skel hc-skel-block">
							<div class="hc-skel-line" />
							<div class="hc-skel-line mid" />
							<div class="hc-skel-line" />
						</div>

						<div
							v-for="group in state.events.groups"
							v-show="!configLoading"
							:key="group.key"
							class="hc-event-group"
						>
							<div class="hc-event-group-head">
								<span class="hc-dot" :class="groupTone(group.tone)" />
								{{ group.label }}
							</div>
							<div
								v-for="item in group.items"
								:key="item.id"
								class="hc-event-row"
							>
								<v-switch
									v-model="item.enabled"
									hide-details
									dense
									color="primary"
									class="mt-0 pt-0"
									@change="confirmEvents"
								/>
								<div>
									<div class="hc-event-name">{{ item.name }}</div>
									<div class="hc-event-source hc-mono">{{ item.source }}</div>
								</div>
								<div class="hc-event-threshold">
									<HaqTextField
										v-model="item.threshold"
										type="number"
										hide-details
										@blur="confirmEvents"
									/>
								</div>
							</div>
						</div>
					</section>

					<!-- Sensors -->
					<section v-show="section === 'sensors'">
						<div class="hc-section-head">
							<div>
								<h1 class="hc-title">Sensors</h1>
								<p class="hc-subtitle">Calibration and audio settings.</p>
							</div>
							<span v-if="configLoading" class="hc-inline-load">
								<v-progress-circular indeterminate size="16" width="2" color="primary" />
								Loading
							</span>
						</div>
						<div v-if="configLoading" class="hc-grid-2">
							<div v-for="n in 3" :key="'sskel-' + n" class="hc-card hc-skel hc-skel-block">
								<div class="hc-skel-line short" />
								<div class="hc-skel-line" />
								<div class="hc-skel-line mid" />
							</div>
						</div>
						<div v-else class="hc-sensors-layout">
							<div class="hc-card">
								<div class="hc-panel-title"><h3>Audio</h3></div>
								<HaqTextField v-model="state.sensors.volumeDb" label="Volume dB" />
								<HaqTextField
									v-model="state.sensors.micLevelL"
									label="Mic level L"
									class="mt-4"
								/>
								<HaqTextField
									v-model="state.sensors.micLevelR"
									label="Mic level R"
									class="mt-4"
								/>
								<HaqTextField
									v-model="state.sensors.micGainL"
									label="Mic gain L"
									class="mt-4"
								/>
								<HaqTextField
									v-model="state.sensors.micGainR"
									label="Mic gain R"
									class="mt-4"
								/>
								<HaqTextField
									v-model="state.sensors.micDbOffset"
									label="dB offset"
									class="mt-4"
								/>
							</div>
							<div class="hc-sensors-side">
								<div class="hc-card">
									<div class="hc-panel-title"><h3>Calibration</h3></div>
									<HaqTextField
										v-model="state.sensors.tempOffsetC"
										label="Temperature offset (°C)"
									/>
								</div>
								<div class="hc-card">
									<div class="hc-panel-title"><h3>Amplify wireless sensors</h3></div>
									<v-switch
										v-model="state.sensors.amplifyEnabled"
										label="Enabled"
										color="primary"
										hide-details
										dense
										class="mt-0"
									/>
									<p class="hc-subtitle mb-0 mt-2">{{ state.sensors.amplifyNote }}</p>
								</div>
							</div>
						</div>
						<div class="hc-footer-row">
							<span />
							<v-btn
								class="haq-btn haq-btn-primary"
								:loading="sensorsSaving"
								@click="saveSensors"
							>
								<v-icon left small>mdi-content-save</v-icon>
								Save changes
							</v-btn>
						</div>
					</section>

					<!-- Network -->
					<section v-show="section === 'network'">
						<div class="hc-section-head">
							<h1 class="hc-title mb-0">Network</h1>
							<span v-if="networkLoading" class="hc-inline-load">
								<v-progress-circular indeterminate size="16" width="2" color="primary" />
								Loading
							</span>
						</div>
						<div v-if="networkLoading" class="hc-grid-2">
							<div v-for="n in 2" :key="'nskel-' + n" class="hc-card hc-skel hc-skel-block">
								<div class="hc-skel-line short" />
								<div class="hc-skel-line" />
								<div class="hc-skel-line mid" />
								<div class="hc-skel-line" />
							</div>
						</div>
						<div v-else class="hc-grid-2">
							<div class="hc-card">
								<div class="hc-panel-title">
									<h3>Network</h3>
								</div>
								<p class="hc-hint mb-3">
									The device API has no DHCP/static field and no network PUT.
									Assignment is console-only. IP/MAC/subnet come from live interface readouts.
								</p>
								<HaqSelect
									v-model="state.device.assignment"
									:items="state.assignmentOptions || ['Unknown', 'DHCP', 'Static']"
									label="Assignment"
									hide-details
									class="mb-4"
								/>
								<div class="hc-grid-2">
									<HaqTextField v-model="state.device.ip" label="IP" />
									<HaqTextField v-model="state.device.mac" label="MAC" />
									<HaqTextField v-model="state.device.subnet" label="Subnet" class="mt-4" />
									<HaqTextField v-model="state.device.gateway" label="Gateway" class="mt-4" />
									<HaqTextField v-model="state.device.link" label="Link" class="mt-4" />
								</div>
								<div class="hc-footer-row">
									<span />
									<v-btn
										class="haq-btn haq-btn-primary"
										:loading="networkSaving"
										@click="saveNetwork"
									>
										<v-icon left small>mdi-content-save</v-icon>
										Save changes
									</v-btn>
								</div>
							</div>
							<div class="hc-card">
								<div class="hc-panel-title">
									<h3>Time &amp; NTP</h3>
								</div>
								<p class="hc-hint mb-3">Applied to the device clock immediately</p>
								<span v-if="state.device.clockInSync" class="hc-confirmed mb-3 d-inline-flex">
									<v-icon small color="success">mdi-check</v-icon>
									Clock in sync
								</span>
								<HaqTextField
									v-model="state.device.timezone"
									label="Timezone"
								/>
								<v-switch
									v-model="state.device.ntpEnabled"
									label="NTP client"
									color="primary"
									hide-details
									dense
									class="mt-4"
								/>
								<HaqTextField
									v-model="state.device.ntpServer"
									label="NTP server"
									class="mt-4"
								/>
								<div class="hc-footer-row">
									<span />
									<v-btn
										class="haq-btn haq-btn-primary"
										:loading="ntpSaving"
										@click="saveNtp"
									>
										<v-icon left small>mdi-content-save</v-icon>
										Save changes
									</v-btn>
								</div>
							</div>
						</div>
					</section>

					<!-- Advanced -->
					<section v-show="section === 'advanced'">
						<div class="hc-section-head">
							<div>
								<h1 class="hc-title">Advanced</h1>
								<p class="hc-subtitle mb-0">
									Changing these can take the device off the network.
								</p>
							</div>
							<span v-if="configLoading" class="hc-inline-load">
								<v-progress-circular indeterminate size="16" width="2" color="primary" />
								Loading
							</span>
						</div>
						<div v-if="configLoading" class="hc-grid-2 mb-4">
							<div v-for="n in 3" :key="'askel-' + n" class="hc-card hc-skel hc-skel-block">
								<div class="hc-skel-line short" />
								<div class="hc-skel-line" />
								<div class="hc-skel-line mid" />
							</div>
						</div>
						<div v-else class="hc-grid-2 mb-4">
							<div class="hc-card">
								<div class="hc-panel-title"><h3>BACnet</h3></div>
								<HaqTextField v-model="state.advanced.bacnet.instance" label="Instance" />
								<HaqTextField
									v-model="state.advanced.bacnet.port"
									label="Port"
									class="mt-4"
								/>
								<HaqTextField
									v-model="state.advanced.bacnet.deviceName"
									label="BACnet device name"
									class="mt-4"
								/>
								<HaqTextField
									v-model="state.advanced.bacnet.covIncrement"
									label="COV increment"
									class="mt-4"
								/>
								<HaqTextField
									v-model="state.advanced.bacnet.location"
									label="BACnet location"
									class="mt-4"
								/>
								<div class="hc-panel-title mt-4"><h3>BBMD / foreign device</h3></div>
								<v-switch
									v-model="state.advanced.bacnet.foreignDevice"
									label="Foreign device"
									color="primary"
									hide-details
									dense
									class="mt-0"
								/>
								<HaqTextField
									v-model="state.advanced.bacnet.foreignBBMD"
									label="Foreign BBMD"
									class="mt-4"
								/>
								<HaqTextField
									v-model="state.advanced.bacnet.foreignTTL"
									label="Foreign TTL"
									class="mt-4"
								/>
							</div>
							<div class="hc-card">
								<div class="hc-panel-title"><h3>SNMP</h3></div>
								<v-switch
									v-model="state.advanced.snmp.enabled"
									label="Enabled"
									color="primary"
									hide-details
									dense
									class="mt-0 mb-4"
								/>
								<HaqTextField v-model="state.advanced.snmp.community" label="RW community" />
								<HaqTextField
									v-model="state.advanced.snmp.username"
									label="RW username"
									class="mt-4"
								/>
								<HaqTextField
									v-model="state.advanced.snmp.rocommunity"
									label="RO community"
									class="mt-4"
								/>
								<HaqTextField
									v-model="state.advanced.snmp.rousername"
									label="RO username"
									class="mt-4"
								/>
								<HaqTextField
									v-model="state.advanced.snmp.location"
									label="Location"
									class="mt-4"
								/>
								<HaqTextField
									v-model="state.advanced.snmp.contact"
									label="Contact"
									class="mt-4"
								/>
							</div>
							<div class="hc-card">
								<div class="hc-panel-title"><h3>Web server &amp; TLS</h3></div>
								<HaqTextField
									v-model="state.advanced.web.httpsPort"
									label="HTTPS port"
								/>
								<HaqTextField
									v-model="state.advanced.web.realm"
									label="Realm"
									class="mt-4"
								/>
								<v-switch
									v-model="state.advanced.web.redirectHttp"
									label="Redirect HTTP"
									color="primary"
									hide-details
									dense
									class="mt-4"
								/>
							</div>
						</div>
						<div class="hc-footer-row mb-6">
							<span />
							<v-btn
								class="haq-btn haq-btn-primary"
								:loading="advancedSaving"
								@click="saveAdvanced"
							>
								<v-icon left small>mdi-content-save</v-icon>
								Save changes
							</v-btn>
						</div>
						<div class="hc-danger">
							<h3>Danger zone</h3>
							<div class="hc-actions">
								<v-btn
									class="haq-btn haq-btn-secondary"
									@click="openConfirm('resetDefaults')"
								>
									Reset defaults
								</v-btn>
								<v-btn
									class="haq-btn haq-btn-danger"
									@click="openConfirm('factoryReset')"
								>
									Factory reset
								</v-btn>
							</div>
						</div>
					</section>
				</main>
			</div>

			<v-dialog v-model="identityModalOpen" content-class="haq-dialog" max-width="720" persistent scrollable>
				<v-card class="haq-dialog-card">
					<v-card-title class="haq-dialog-card-header">
						<span>Edit info</span>
						<v-spacer />
						<v-btn icon :disabled="identitySaving" @click="identityModalOpen = false">
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</v-card-title>
					<v-card-text>
						<v-row dense>
							<v-col cols="12" sm="6">
								<HaqTextField v-model="identityDraft.name" label="Device name" />
							</v-col>
							<v-col cols="12" sm="6">
								<HaqTextField v-model="identityDraft.st_addr" label="Street address" />
							</v-col>
							<v-col cols="12" sm="6">
								<HaqTextField v-model="identityDraft.wing" label="Wing" class="mt-4" />
							</v-col>
							<v-col cols="12" sm="6">
								<HaqTextField v-model="identityDraft.floor" label="Floor" class="mt-4" />
							</v-col>
							<v-col cols="12" sm="6">
								<HaqTextField v-model="identityDraft.room" label="Room" class="mt-4" />
							</v-col>
							<v-col cols="12" sm="6">
								<HaqTextField v-model="identityDraft.desc" label="Description" class="mt-4" />
							</v-col>
							<v-col cols="12" sm="6">
								<HaqTextField v-model="identityDraft.latitude" label="Latitude" class="mt-4" />
							</v-col>
							<v-col cols="12" sm="6">
								<HaqTextField v-model="identityDraft.longitude" label="Longitude" class="mt-4" />
							</v-col>
						</v-row>
					</v-card-text>
					<v-card-actions>
						<v-spacer />
						<v-btn
							class="haq-btn haq-btn-secondary"
							:disabled="identitySaving"
							@click="identityModalOpen = false"
						>
							Cancel
						</v-btn>
						<v-btn
							class="haq-btn haq-btn-primary"
							:loading="identitySaving"
							@click="saveIdentity"
						>
							<v-icon left small>mdi-content-save</v-icon>
							Save
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>

			<v-dialog v-model="presetsModalOpen" content-class="haq-dialog" max-width="520" persistent>
				<v-card class="haq-dialog-card">
					<v-card-title class="haq-dialog-card-header">
						<span>Presets</span>
						<v-spacer />
						<v-btn icon :disabled="presetApplying" @click="presetsModalOpen = false">
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</v-card-title>
					<v-card-text>
						<p class="hc-hint mb-3">
							Applying a preset can change many device settings at once.
						</p>
						<HaqSelect
							v-model="presetDraft.selected"
							:items="(state.presets && state.presets.names) || []"
							label="Preset"
							hide-details
							class="mb-4"
						/>
						<HaqSelect
							v-model="presetDraft.mode"
							:items="state.presetModeOptions || ['Overwrite', 'Merge', 'Keep']"
							label="Apply mode"
							hide-details
						/>
					</v-card-text>
					<v-card-actions>
						<v-spacer />
						<v-btn
							class="haq-btn haq-btn-secondary"
							:disabled="presetApplying"
							@click="presetsModalOpen = false"
						>
							Cancel
						</v-btn>
						<v-btn
							class="haq-btn haq-btn-primary"
							:loading="presetApplying"
							:disabled="!presetDraft.selected"
							@click="openConfirm('preset')"
						>
							Apply preset
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>

			<v-dialog v-model="confirmOpen" content-class="haq-dialog" max-width="480" persistent>
				<v-card class="haq-dialog-card">
					<v-card-title>{{ confirmTitle }}</v-card-title>
					<v-card-text>{{ confirmBody }}</v-card-text>
					<v-card-actions>
						<v-spacer />
						<v-btn class="haq-btn haq-btn-secondary" @click="confirmOpen = false">Cancel</v-btn>
						<v-btn
							class="haq-btn"
							:class="confirmKind === 'factoryReset' ? 'haq-btn-danger' : 'haq-btn-primary'"
							@click="runConfirm"
						>
							{{ confirmActionLabel }}
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>
		</div>
	</v-dialog>
</template>

<script>
import HaqAutocomplete from '@/components/form/HaqAutocomplete.vue';
import HaqSelect from '@/components/form/HaqSelect.vue';
import HaqTextField from '@/components/form/HaqTextField.vue';
import { createMockHaloConsoleState, mockConfirmSave } from './mockHaloConsole';
import { runHaloHttp, putAndConfirm, patchHaloReadOnlyMode } from './haloEdgeClient';
import {
	HALO_PATHS,
	applyLiveSlices,
	bacnetPutBody,
	emptyConsoleState,
	eventsPutBody,
	formatIdentLocation,
	execPath,
	identPutBody,
	integrationPutBody,
	isHaloReadOnlyMode,
	locateForceBody,
	mapEvents,
	mapIntegration,
	parseVerificationKey,
	presetApplyPath,
	settimeinfoPath,
	snmpPutBody,
	webPutBody,
} from './haloGstate';

/**
 * Fullscreen HALO Device Console — Phase 2 live Edge jobs when installation + HTTP device present.
 */
export default {
	name: 'HaloConsole',
	components: {
		HaqAutocomplete,
		HaqSelect,
		HaqTextField,
	},
	props: {
		/** Dialog open state (v-model) */
		value: {
			type: Boolean,
			default: false,
		},
		/** Edge device row from Virtual Edge devices table */
		edgeDevice: {
			type: Object,
			default: null,
		},
		/** Parent EdgeInstallation row */
		installation: {
			type: Object,
			default: null,
		},
		/** Optional fleet list from the installation devices table */
		fleetDevices: {
			type: Array,
			default: () => [],
		},
	},
	data() {
		return {
			section: 'overview',
			state: null,
			selectedFleetId: null,
			activeDevice: null,
			liveMode: false,
			liveError: null,
			loadToken: 0,
			sliceLoading: {
				versions: false,
				config: false,
				latest: false,
				cloud: false,
				https: false,
				eventState: false,
				timeok: false,
				presets: false,
			},
			sliceLoaded: {
				versions: false,
				config: false,
				latest: false,
				cloud: false,
				https: false,
				eventState: false,
				timeok: false,
				presets: false,
			},
			finding: false,
			refreshing: false,
			identityModalOpen: false,
			presetsModalOpen: false,
			identityDraft: {
				name: '',
				wing: '',
				floor: '',
				room: '',
				st_addr: '',
				desc: '',
				latitude: '',
				longitude: '',
				student_count: '',
				_raw: null,
			},
			presetDraft: {
				selected: '',
				mode: 'Overwrite',
			},
			identitySaving: false,
			presetApplying: false,
			integrationSaving: false,
			authExpanded: false,
			sensorsSaving: false,
			ntpSaving: false,
			networkSaving: false,
			readOnlyMode: false,
			readOnlySaving: false,
			advancedSaving: false,
			eventsSaving: false,
			confirmOpen: false,
			confirmKind: null,
			primaryNav: [
				{ id: 'overview', label: 'Overview', icon: 'mdi-view-dashboard-outline' },
				{ id: 'integrations', label: 'Integrations', icon: 'mdi-lan-connect' },
				{ id: 'events', label: 'Events', icon: 'mdi-flash-outline' },
				{ id: 'sensors', label: 'Sensors', icon: 'mdi-access-point' },
				{ id: 'network', label: 'Network', icon: 'mdi-ethernet' },
			],
		};
	},
	computed: {
		/** @returns {string|null} */
		installationId() {
			return this.installation?.id || null;
		},
		/**
		 * Confirm dialog title for the pending dangerous action.
		 * @returns {string}
		 */
		confirmTitle() {
			if (this.confirmKind === 'restart') return 'Restart device?';
			if (this.confirmKind === 'resetDefaults') return 'Reset defaults?';
			if (this.confirmKind === 'factoryReset') return 'Factory reset?';
			if (this.confirmKind === 'preset') return 'Apply preset?';
			return '';
		},
		/**
		 * Plain-language confirm body.
		 * @returns {string}
		 */
		confirmBody() {
			if (this.confirmKind === 'restart') {
				return "The device goes offline for about a minute while it restarts. Events won't be captured during that time.";
			}
			if (this.confirmKind === 'resetDefaults') {
				return 'This replaces the current configuration with the selected preset defaults. Existing custom settings may be lost.';
			}
			if (this.confirmKind === 'factoryReset') {
				return 'This erases device configuration and returns the sensor to factory settings. You will need to reconfigure integrations and events afterward.';
			}
			if (this.confirmKind === 'preset') {
				const name = this.presetDraft?.selected || this.state?.presets?.selected || 'this preset';
				const mode = this.presetDraft?.mode || this.state?.presets?.mode || 'Overwrite';
				return `Apply “${name}” with mode ${mode}? This can change many settings on the device at once.`;
			}
			return '';
		},
		/**
		 * Primary action label on the confirm dialog.
		 * @returns {string}
		 */
		confirmActionLabel() {
			if (this.confirmKind === 'restart') return 'Restart';
			if (this.confirmKind === 'resetDefaults') return 'Reset defaults';
			if (this.confirmKind === 'factoryReset') return 'Factory reset';
			if (this.confirmKind === 'preset') return 'Apply preset';
			return 'Confirm';
		},
		/**
		 * Autocomplete items: HTTP EdgeDevices only (or mock fleet when none loaded).
		 * @returns {Array<{id:string,name:string}>}
		 */
		fleetItems() {
			if (!this.state) return [];
			if (this.fleetDevices && this.fleetDevices.length) {
				return this.fleetDevices
					.filter((d) => {
						const protocol = String(d.protocol || '').toLowerCase();
						if (protocol !== 'http' && protocol !== 'https') return false;
						const product = String(d.product || (d.attrs && d.attrs.product) || '').toLowerCase();
						const tags = d.tags || (d.attrs && d.attrs.tags) || [];
						const tagged = Array.isArray(tags)
							&& tags.some((t) => String(t).toLowerCase() === 'halo');
						return product === 'halo' || (d.attrs && d.attrs.isHalo === true) || tagged;
					})
					.map((d) => ({
						id: d.id || d.key,
						name: d.name && d.name !== '—' ? d.name : (d.key || d.id),
					}));
			}
			return this.state.fleet || [];
		},
		/**
		 * Health strip cards for Overview.
		 * @returns {Array}
		 */
		healthCards() {
			const h = this.state?.health;
			if (!h) return [];
			return [
				{ key: 'cloud', label: 'Cloud', ...h.cloud },
				{ key: 'heartbeat', label: 'Heartbeat', ...h.heartbeat },
				{ key: 'primary', label: 'Primary integration', ...h.primary },
				{ key: 'events', label: 'Events firing', ...h.events },
			];
		},
		/**
		 * Read-only device facts row.
		 * @returns {Array<{label:string,value:string}>}
		 */
		deviceFacts() {
			const d = this.state?.device;
			if (!d) return [];
			return [
				{ label: 'Firmware', value: d.firmware },
				{ label: 'IP', value: d.ip },
				{ label: 'MAC', value: d.mac },
				{ label: 'Serial', value: d.serial },
				{ label: 'Uptime', value: d.uptime },
			];
		},
		/** @returns {Object} */
		integrations() {
			return this.state?.integrations;
		},
		/**
		 * Currently selected integration channel config.
		 * @returns {Object}
		 */
		activeIntegration() {
			const tab = this.integrations?.activeTab || 'heartbeat';
			return this.integrations?.[tab] || {};
		},
		/**
		 * Integration tab descriptors.
		 * @returns {Array}
		 */
		integrationTabs() {
			const i = this.integrations;
			if (!i) return [];
			return [
				{ id: 'primary', label: 'Primary', status: i.primary.status, statusLabel: i.primary.statusLabel },
				{ id: 'secondary', label: 'Secondary', status: i.secondary.status, statusLabel: i.secondary.statusLabel },
				{ id: 'heartbeat', label: 'Heartbeat', status: i.heartbeat.status, statusLabel: i.heartbeat.statusLabel },
			];
		},
		/**
		 * Count of enabled event rows.
		 * @returns {number}
		 */
		enabledEventCount() {
			if (!this.state?.events?.groups) return 0;
			return this.state.events.groups.reduce(
				(sum, g) => sum + g.items.filter((x) => x.enabled).length,
				0
			);
		},
		/** @returns {boolean} */
		healthLoading() {
			return this.liveMode && (!this.sliceLoaded.cloud || !this.sliceLoaded.config);
		},
		/** @returns {boolean} */
		readingsLoading() {
			return this.liveMode && !this.sliceLoaded.latest;
		},
		/** @returns {boolean} */
		factsLoading() {
			return this.liveMode && !this.sliceLoaded.versions;
		},
		/** @returns {boolean} */
		configLoading() {
			return this.liveMode && !this.sliceLoaded.config;
		},
		/** @returns {boolean} */
		networkLoading() {
			return this.liveMode && !this.sliceLoaded.versions;
		},
	},
	watch: {
		value: {
			immediate: true,
			handler(open) {
				if (open) this.bootstrap();
			},
		},
		async selectedFleetId(id, prev) {
			if (!this.state || !id || id === prev) return;
			const row = (this.fleetDevices || []).find((d) => (d.id || d.key) === id);
			if (row && this.liveMode) {
				this.activeDevice = row;
				await this.loadLiveDevice(row);
			} else if (row) {
				this.state.device.name = row.name && row.name !== '—' ? row.name : row.key;
				this.state.device.id = row.id || row.key;
				this.syncReadOnlyFromDevice(row);
			}
		},
	},
	methods: {
		/**
		 * If Read Only Mode is on, toast and skip the mutating job.
		 * @returns {boolean}
		 */
		skipReadOnlyWrite() {
			if (!this.readOnlyMode) return false;
			window.M?.toast?.({ html: 'Not sent — read only mode' });
			return true;
		},
		/**
		 * Copy read-only metadata onto an EdgeDevice object for the current session.
		 * @param {Object} device
		 * @param {boolean} enabled
		 */
		applyReadOnlyToDevice(device, enabled) {
			if (!device) return;
			this.$set(device, 'readOnlyMode', !!enabled);
			const attrs = device.attrs && typeof device.attrs === 'object' ? device.attrs : {};
			this.$set(device, 'attrs', { ...attrs, readOnlyMode: !!enabled });
		},
		/**
		 * Load the persisted read-only flag for the current device (default off).
		 * @param {Object} device
		 */
		syncReadOnlyFromDevice(device) {
			this.readOnlyMode = isHaloReadOnlyMode(device);
		},
		/**
		 * Persist Read Only Mode on this Halo's EdgeDevice metadata.
		 */
		async onReadOnlyModeChange() {
			const next = !!this.readOnlyMode;
			const device = this.activeDevice || this.edgeDevice;
			if (!this.installationId || !device || !device.id) {
				this.applyReadOnlyToDevice(device, next);
				return;
			}
			this.readOnlySaving = true;
			try {
				await patchHaloReadOnlyMode(this.installationId, device.id, next);
				this.applyReadOnlyToDevice(device, next);
				this.$emit('halo-attrs', { deviceId: device.id, readOnlyMode: next });
			} catch (error) {
				console.error(error);
				this.readOnlyMode = !next;
				window.M?.toast?.({ html: error.message || 'Could not save read only mode' });
			} finally {
				this.readOnlySaving = false;
			}
		},
		/**
		 * Reset per-slice loading flags.
		 * @param {boolean} loading
		 */
		resetSliceFlags(loading = false) {
			this._sliceInflight = {};
			Object.keys(this.sliceLoading).forEach((k) => {
				this.$set(this.sliceLoading, k, loading);
				this.$set(this.sliceLoaded, k, false);
			});
		},
		/**
		 * Navigate rail section and ensure its data is requested.
		 * @param {string} id
		 */
		goSection(id) {
			this.section = id;
			this.ensureSection(id);
		},
		/**
		 * Lazy-request slices needed by a section (no-op if already loaded/loading).
		 * @param {string} id
		 */
		ensureSection(id) {
			if (!this.liveMode || !this.activeDevice) return;
			if (id === 'network') {
				this.fetchSlice('timeok', HALO_PATHS.timeok);
				this.fetchSlice('versions', HALO_PATHS.versions);
			} else if (id === 'integrations') {
				this.fetchSlice('config', HALO_PATHS.config);
				this.fetchSlice('https', HALO_PATHS.https);
			} else if (id === 'events' || id === 'sensors' || id === 'advanced') {
				this.fetchSlice('config', HALO_PATHS.config);
			}
		},
		/**
		 * Open console: live Edge jobs when possible, else mock.
		 */
		async bootstrap() {
			this.section = 'overview';
			this.authExpanded = false;
			this.confirmOpen = false;
			this.confirmKind = null;
			this.liveError = null;
			this.loadToken += 1;

			const canLive = !!(
				this.installationId
				&& this.edgeDevice
				&& String(this.edgeDevice.protocol || '').toLowerCase() === 'http'
				&& this.edgeDevice.attrs
				&& this.edgeDevice.attrs.host
			);

			this.liveMode = canLive;
			this.activeDevice = canLive ? this.edgeDevice : null;
			this.selectedFleetId = this.edgeDevice?.id || this.edgeDevice?.key || null;
			this.syncReadOnlyFromDevice(this.activeDevice || this.edgeDevice);

			if (canLive) {
				this.state = emptyConsoleState(this.edgeDevice);
				this.loadLiveDevice(this.edgeDevice);
			} else {
				this.resetSliceFlags(false);
				this.state = createMockHaloConsoleState();
				const edgeName = this.edgeDevice?.name && this.edgeDevice.name !== '—'
					? this.edgeDevice.name
					: this.edgeDevice?.key;
				if (edgeName) {
					this.state.device.name = edgeName;
					this.state.device.id = this.edgeDevice.id || this.edgeDevice.key || 'mock-1';
				}
				if (this.fleetDevices?.length) {
					this.selectedFleetId = this.state.device.id;
				} else {
					this.selectedFleetId = this.state.fleet[0].id;
				}
			}
		},
		/**
		 * Progressive live load: overview first (parallel), then background slices.
		 * @param {Object} device
		 */
		async loadLiveDevice(device) {
			const token = ++this.loadToken;
			this.liveError = null;
			this.state = emptyConsoleState(device);
			this.state.device.id = device.id || device.key;
			this.syncReadOnlyFromDevice(device);
			this.resetSliceFlags(false);

			// Wave 1 — Overview panels fill as each Edge job completes
			const primary = [
				['versions', HALO_PATHS.versions],
				['config', HALO_PATHS.config],
				['latest', HALO_PATHS.latest],
				['cloud', HALO_PATHS.cloud],
			];
			await Promise.all(primary.map(([key, path]) => this.fetchSlice(key, path, token, device)));
			if (token !== this.loadToken) return;

			// Wave 2 — remaining slices (integrations TLS, events badge, NTP sync)
			const secondary = [
				['https', HALO_PATHS.https],
				['eventState', HALO_PATHS.eventState],
				['timeok', HALO_PATHS.timeok],
			];
			await Promise.all(secondary.map(([key, path]) => this.fetchSlice(key, path, token, device)));
			if (token !== this.loadToken) return;
			await this.fetchSlice('presets', HALO_PATHS.presets, token, device, { soft: true });
		},
		/**
		 * Fetch one gstate slice and merge into UI when it arrives.
		 * @param {string} key
		 * @param {string} path
		 * @param {number} [token]
		 * @param {Object} [device]
		 * @param {{ soft?: boolean }} [opts] soft failures (e.g. presets) do not banner the console
		 * @returns {Promise<void>}
		 */
		fetchSlice(key, path, token = this.loadToken, device = this.activeDevice, opts = {}) {
			if (!this.liveMode || !device) return Promise.resolve();
			if (this.sliceLoaded[key]) return Promise.resolve();
			if (!this._sliceInflight) this._sliceInflight = {};
			if (this._sliceInflight[key]) return this._sliceInflight[key];

			this.$set(this.sliceLoading, key, true);
			const work = (async () => {
				try {
					const body = await runHaloHttp(this.installationId, device, { path });
					if (token !== this.loadToken) return;
					this.state = applyLiveSlices(this.state, { [key]: body }, device);
				} catch (error) {
					console.error(error);
					if (token === this.loadToken && !opts.soft) {
						const msg = error.message || `Failed to load ${key}`;
						this.liveError = msg;
						window.M?.toast?.({ html: msg });
					}
				} finally {
					if (this._sliceInflight) delete this._sliceInflight[key];
					if (token === this.loadToken) {
						this.$set(this.sliceLoaded, key, true);
						this.$set(this.sliceLoading, key, false);
					}
				}
			})();
			this._sliceInflight[key] = work;
			return work;
		},
		/**
		 * Close the fullscreen dialog.
		 */
		close() {
			this.loadToken += 1;
			this.$emit('input', false);
			this.$emit('close');
		},
		/**
		 * Switch Integrations tab and collapse auth.
		 * @param {string} tabId
		 */
		onIntegrationTab(tabId) {
			this.integrations.activeTab = tabId;
			this.authExpanded = false;
		},
		/**
		 * Map status token to card CSS modifier.
		 * @param {string} status
		 * @returns {string}
		 */
		statusClass(status) {
			if (status === 'error') return 'is-error';
			if (status === 'warn') return 'is-warn';
			return '';
		},
		/**
		 * Map event group tone to status dot class.
		 * @param {string} tone
		 * @returns {string}
		 */
		groupTone(tone) {
			if (tone === 'danger') return 'error';
			if (tone === 'warn') return 'warn';
			return 'ok';
		},
		/**
		 * Re-fetch overview (and related) live slices, or remount mock state.
		 */
		async refreshOverview() {
			if (this.refreshing) return;
			this.refreshing = true;
			this.liveError = null;
			try {
				if (this.liveMode && this.activeDevice) {
					await this.loadLiveDevice(this.activeDevice);
				} else {
					await mockConfirmSave(400);
					this.state = createMockHaloConsoleState();
				}
			} catch (error) {
				console.error(error);
				this.liveError = error.message || 'Refresh failed';
				window.M?.toast?.({ html: this.liveError });
			} finally {
				this.refreshing = false;
			}
		},
		/**
		 * Locate LED pulse (short secs) or mock delay.
		 */
		async findDevice() {
			this.finding = true;
			window.M?.toast?.({ html: 'Finding device…' });
			try {
				if (this.skipReadOnlyWrite()) {
					await mockConfirmSave(400);
					return;
				}
				if (this.liveMode) {
					await runHaloHttp(this.installationId, this.activeDevice, {
						path: HALO_PATHS.force,
						method: 'PUT',
						body: locateForceBody(),
						timeoutMs: 10000,
					});
					window.M?.toast?.({ html: 'Locate finished' });
				} else {
					await mockConfirmSave(2200);
					window.M?.toast?.({ html: 'Locate finished' });
				}
			} catch (error) {
				console.error(error);
				window.M?.toast?.({ html: error.message || 'Locate failed' });
			} finally {
				this.finding = false;
			}
		},
		/**
		 * Open a plain-language confirm dialog for a risky action.
		 * @param {'restart'|'resetDefaults'|'factoryReset'} kind
		 */
		openConfirm(kind) {
			this.confirmKind = kind;
			this.confirmOpen = true;
		},
		/**
		 * Run the confirmed action. Lab Halo stays write-protected.
		 */
		async runConfirm() {
			const kind = this.confirmKind;
			this.confirmOpen = false;
			this.confirmKind = null;
			if (kind === 'preset') {
				await this.applyPreset();
				return;
			}
			if (this.skipReadOnlyWrite()) {
				await mockConfirmSave(300);
				return;
			}
			if (this.liveMode && (kind === 'restart' || kind === 'resetDefaults' || kind === 'factoryReset')) {
				try {
					await this.runDangerCommand(kind);
				} catch (error) {
					console.error(error);
					window.M?.toast?.({ html: error.message || 'Command failed' });
				}
				return;
			}
			await mockConfirmSave(500);
			if (kind === 'restart') {
				window.M?.toast?.({ html: 'Restart requested' });
			} else if (kind === 'resetDefaults') {
				window.M?.toast?.({ html: 'Reset to defaults requested' });
			} else if (kind === 'factoryReset') {
				window.M?.toast?.({ html: 'Factory reset requested' });
			}
		},
		/**
		 * Reboot or reset-to-defaults on a non-lab Halo.
		 * @param {'restart'|'resetDefaults'|'factoryReset'} kind
		 */
		async runDangerCommand(kind) {
			if (kind === 'restart') {
				const raw = await runHaloHttp(this.installationId, this.activeDevice, {
					path: HALO_PATHS.verification,
					timeoutMs: 15000,
				});
				const key = parseVerificationKey(raw)
					|| parseVerificationKey(this.state._raw?.config?.api);
				if (!key) throw new Error('Missing device verification key');
				await runHaloHttp(this.installationId, this.activeDevice, {
					path: execPath('reboot', key),
					timeoutMs: 20000,
				});
				window.M?.toast?.({ html: 'Restart requested' });
				return;
			}
			await runHaloHttp(this.installationId, this.activeDevice, {
				path: HALO_PATHS.configReset,
				timeoutMs: 20000,
			});
			window.M?.toast?.({
				html: kind === 'factoryReset' ? 'Factory reset requested' : 'Reset to defaults requested',
			});
			await this.loadLiveDevice(this.activeDevice);
		},
		/**
		 * Open identity editor modal with a draft copy.
		 */
		openIdentityModal() {
			const id = this.state?.identity || {};
			this.identityDraft = {
				name: id.name || this.state?.device?.name || '',
				wing: id.wing || '',
				floor: id.floor || '',
				room: id.room || '',
				st_addr: id.st_addr || '',
				desc: id.desc || '',
				latitude: id.latitude || '',
				longitude: id.longitude || '',
				student_count: id.student_count || '',
				_raw: id._raw || null,
			};
			this.identityModalOpen = true;
		},
		/**
		 * Open presets modal with a draft selection.
		 */
		openPresetsModal() {
			const p = this.state?.presets || {};
			this.presetDraft = {
				selected: p.selected || (p.names && p.names[0]) || '',
				mode: p.mode || 'Overwrite',
			};
			this.presetsModalOpen = true;
		},
		/**
		 * Save Halo device name + ident location fields from the identity modal.
		 */
		async saveIdentity() {
			this.identitySaving = true;
			try {
				const id = this.identityDraft;
				if (this.skipReadOnlyWrite()) {
					await mockConfirmSave(300);
					this.state.identity = { ...id };
					this.state.device.name = id.name;
					this.state.device.location = formatIdentLocation(id);
					this.identityModalOpen = false;
					return;
				}
				if (this.liveMode) {
					await putAndConfirm(this.installationId, this.activeDevice, HALO_PATHS.config, {
						devicename: id.name || '',
					});
					await new Promise((r) => setTimeout(r, 900));
					const identBody = await putAndConfirm(
						this.installationId,
						this.activeDevice,
						HALO_PATHS.ident,
						identPutBody(id)
					);
					const mergedIdent = identBody && typeof identBody === 'object' ? identBody : identPutBody(id);
					this.state.identity = {
						name: id.name || '',
						wing: mergedIdent.wing != null ? String(mergedIdent.wing) : '',
						floor: mergedIdent.floor != null ? String(mergedIdent.floor) : '',
						room: mergedIdent.room != null ? String(mergedIdent.room) : '',
						st_addr: mergedIdent.st_addr != null ? String(mergedIdent.st_addr) : '',
						desc: mergedIdent.desc != null ? String(mergedIdent.desc) : '',
						latitude: mergedIdent.latitude != null ? String(mergedIdent.latitude) : '',
						longitude: mergedIdent.longitude != null ? String(mergedIdent.longitude) : '',
						student_count: mergedIdent.student_count != null ? String(mergedIdent.student_count) : '',
						_raw: mergedIdent,
					};
					this.state.device.name = id.name || this.state.device.name;
					this.state.device.location = formatIdentLocation(this.state.identity);
					if (this.state._raw?.config) {
						this.state._raw.config.devicename = id.name;
						this.state._raw.config.ident = mergedIdent;
					}
					window.M?.toast?.({ html: 'Identity saved on device' });
				} else {
					await mockConfirmSave();
					this.state.identity = { ...id };
					this.state.device.name = id.name;
					this.state.device.location = formatIdentLocation(id);
				}
				this.identityModalOpen = false;
			} catch (error) {
				console.error(error);
				window.M?.toast?.({ html: error.message || 'Identity save failed' });
			} finally {
				this.identitySaving = false;
			}
		},
		/**
		 * Apply the selected preset on the device (GET apply path).
		 */
		async applyPreset() {
			const name = this.presetDraft?.selected || this.state?.presets?.selected;
			const mode = this.presetDraft?.mode || this.state?.presets?.mode || 'Overwrite';
			if (!name) return;
			this.presetApplying = true;
			try {
				if (this.state?.presets) {
					this.state.presets.selected = name;
					this.state.presets.mode = mode;
				}
				if (this.skipReadOnlyWrite()) {
					await mockConfirmSave(300);
					this.presetsModalOpen = false;
					return;
				}
				if (this.liveMode) {
					await runHaloHttp(this.installationId, this.activeDevice, {
						path: presetApplyPath(name, mode),
						method: 'GET',
						timeoutMs: 20000,
					});
					window.M?.toast?.({ html: `Preset “${name}” applied` });
					this.presetsModalOpen = false;
					await this.loadLiveDevice(this.activeDevice);
				} else {
					await mockConfirmSave(800);
					window.M?.toast?.({ html: `Preset “${name}” applied (mock)` });
					this.presetsModalOpen = false;
				}
			} catch (error) {
				console.error(error);
				window.M?.toast?.({ html: error.message || 'Preset apply failed' });
			} finally {
				this.presetApplying = false;
			}
		},
		/**
		 * Save the active integration channel (write + read-back when live).
		 */
		async saveIntegration() {
			this.integrationSaving = true;
			try {
				if (this.skipReadOnlyWrite()) {
					await mockConfirmSave(300);
					return;
				}
				if (this.liveMode) {
					const tab = this.integrations.activeTab;
					const path = tab === 'primary'
						? HALO_PATHS.vms
						: (tab === 'secondary' ? HALO_PATHS.vms2 : HALO_PATHS.heartbeat);
					const body = integrationPutBody(this.activeIntegration, tab);
					const readBack = await putAndConfirm(
						this.installationId,
						this.activeDevice,
						path,
						body
					);
					this.state.integrations[tab] = mapIntegration(
						readBack,
						tab,
						this.state._raw?.https || {}
					);
					window.M?.toast?.({ html: 'Integration saved on device' });
				} else {
					await mockConfirmSave();
				}
			} catch (error) {
				console.error(error);
				window.M?.toast?.({ html: error.message || 'Save failed' });
			} finally {
				this.integrationSaving = false;
			}
		},
		/**
		 * Persist event threshold / enable edits.
		 */
		async confirmEvents() {
			if (this.readOnlyMode) return;
			if (this.eventsSaving) return;
			this.eventsSaving = true;
			try {
				if (this.liveMode) {
					const body = eventsPutBody(this.state.events.raw, this.state.events.groups);
					const readBack = await putAndConfirm(
						this.installationId,
						this.activeDevice,
						HALO_PATHS.eventgen,
						body
					);
					this.state.events = mapEvents(readBack);
				} else {
					await mockConfirmSave(400);
				}
			} catch (error) {
				console.error(error);
				window.M?.toast?.({ html: error.message || 'Events save failed' });
			} finally {
				this.eventsSaving = false;
			}
		},
		/**
		 * Save sensors panel (volume, mics, temp offset, amplify).
		 */
		async saveSensors() {
			this.sensorsSaving = true;
			try {
				if (this.skipReadOnlyWrite()) {
					await mockConfirmSave(300);
					return;
				}
				if (this.liveMode) {
					const s = this.state.sensors;
					const cfg = this.state._raw?.config || {};
					const micsRaw = (cfg.sensor_config && cfg.sensor_config.mics) || {};
					const htRaw = (cfg.sensor_config && cfg.sensor_config.htsensor) || {};
					const bleRaw = (cfg.drivers && cfg.drivers.blebcn && cfg.drivers.blebcn.config) || {};
					await putAndConfirm(this.installationId, this.activeDevice, HALO_PATHS.config, {
						volume: Number(s.volumeDb),
					});
					await new Promise((r) => setTimeout(r, 900));
					await putAndConfirm(this.installationId, this.activeDevice, HALO_PATHS.mics, {
						...micsRaw,
						levelL: Number(s.micLevelL),
						levelR: Number(s.micLevelR),
						gainL: Number(s.micGainL),
						gainR: Number(s.micGainR),
						dboffset: Number(s.micDbOffset),
					});
					await new Promise((r) => setTimeout(r, 900));
					await putAndConfirm(this.installationId, this.activeDevice, HALO_PATHS.htsensor, {
						...htRaw,
						useroffset: Number(s.tempOffsetC),
					});
					await new Promise((r) => setTimeout(r, 900));
					await putAndConfirm(this.installationId, this.activeDevice, HALO_PATHS.blebcn, {
						...bleRaw,
						enabled: !!s.amplifyEnabled,
					});
					window.M?.toast?.({ html: 'Sensors saved on device' });
				} else {
					await mockConfirmSave();
				}
			} catch (error) {
				console.error(error);
				window.M?.toast?.({ html: error.message || 'Sensors save failed' });
			} finally {
				this.sensorsSaving = false;
			}
		},
		/**
		 * Save Network panel (console-only; Halo has no network PUT).
		 */
		async saveNetwork() {
			this.networkSaving = true;
			try {
				await mockConfirmSave(300);
				if (this.readOnlyMode) {
					window.M?.toast?.({ html: 'Not sent — read only mode' });
				} else {
					window.M?.toast?.({ html: 'Network fields kept in the console only' });
				}
			} finally {
				this.networkSaving = false;
			}
		},
		/**
		 * Save Time & NTP. Lab Halo stays write-protected.
		 */
		async saveNtp() {
			this.ntpSaving = true;
			try {
				if (this.skipReadOnlyWrite()) {
					await mockConfirmSave(300);
					this.state.device.clockInSync = true;
					return;
				}
				if (this.liveMode) {
					await runHaloHttp(this.installationId, this.activeDevice, {
						path: settimeinfoPath({
							tz: this.state.device.timezone,
							ntp: this.state.device.ntpEnabled !== false,
							srv: this.state.device.ntpServer,
						}),
						timeoutMs: 15000,
					});
					const timeok = await runHaloHttp(this.installationId, this.activeDevice, {
						path: HALO_PATHS.timeok,
						timeoutMs: 15000,
					});
					this.state.device.clockInSync = timeok === 1 || timeok === true || timeok === '1';
					window.M?.toast?.({ html: 'Time settings saved on device' });
				} else {
					await mockConfirmSave();
					this.state.device.clockInSync = true;
				}
			} catch (error) {
				console.error(error);
				window.M?.toast?.({ html: error.message || 'NTP save failed' });
			} finally {
				this.ntpSaving = false;
			}
		},
		/**
		 * Save advanced BACnet + SNMP; Web/TLS when not lab-blocked.
		 */
		async saveAdvanced() {
			this.advancedSaving = true;
			try {
				if (this.skipReadOnlyWrite()) {
					await mockConfirmSave(300);
					return;
				}
				if (this.liveMode) {
					const bacnetBody = await putAndConfirm(
						this.installationId,
						this.activeDevice,
						HALO_PATHS.bacnet,
						bacnetPutBody(this.state.advanced.bacnet)
					);
					this.state.advanced.bacnet = {
						...this.state.advanced.bacnet,
						...bacnetPutBody({ ...this.state.advanced.bacnet, _raw: bacnetBody }),
						instance: String(bacnetBody.instanceNumber ?? this.state.advanced.bacnet.instance),
						port: String(bacnetBody.port ?? this.state.advanced.bacnet.port),
						deviceName: bacnetBody.deviceName || this.state.advanced.bacnet.deviceName,
						covIncrement: String(bacnetBody.covIncrement ?? this.state.advanced.bacnet.covIncrement),
						location: bacnetBody.location != null ? String(bacnetBody.location) : '',
						foreignDevice: !!bacnetBody.foreignDevice,
						foreignBBMD: bacnetBody.foreignBBMD != null ? String(bacnetBody.foreignBBMD) : '',
						foreignTTL: bacnetBody.foreignTTL != null ? String(bacnetBody.foreignTTL) : '30',
						_raw: bacnetBody,
					};
					await new Promise((r) => setTimeout(r, 900));
					const snmpBody = await putAndConfirm(
						this.installationId,
						this.activeDevice,
						HALO_PATHS.snmp,
						snmpPutBody(this.state.advanced.snmp)
					);
					this.state.advanced.snmp = {
						...this.state.advanced.snmp,
						enabled: !!(snmpBody.on || snmpBody.ena),
						community: snmpBody.community != null ? String(snmpBody.community) : '',
						username: snmpBody.username != null ? String(snmpBody.username) : '',
						rocommunity: snmpBody.rocommunity != null ? String(snmpBody.rocommunity) : '',
						rousername: snmpBody.rousername != null ? String(snmpBody.rousername) : '',
						location: snmpBody.location != null ? String(snmpBody.location) : '',
						contact: snmpBody.contact != null ? String(snmpBody.contact) : '',
						_raw: snmpBody,
					};
					await new Promise((r) => setTimeout(r, 900));
					const webBody = await putAndConfirm(
						this.installationId,
						this.activeDevice,
						HALO_PATHS.api,
						webPutBody(this.state.advanced.web)
					);
					this.state.advanced.web = {
						httpsPort: webBody.httpsport != null ? String(webBody.httpsport) : '',
						realm: webBody.realm || '',
						redirectHttp: webBody.httpredirect === 1 || webBody.httpredirect === true,
						_raw: webBody,
					};
					window.M?.toast?.({ html: 'Advanced settings saved on device' });
				} else {
					await mockConfirmSave();
				}
			} catch (error) {
				console.error(error);
				window.M?.toast?.({ html: error.message || 'Advanced save failed' });
			} finally {
				this.advancedSaving = false;
			}
		},
	},
};
</script>

<style lang="sass">
@import './halo-console.sass'
</style>
